import { useRef } from "react";

/**
 * Picks the source for a media element and keeps it stable for the lifetime of
 * that piece of media.
 *
 * The media cache resolves its blob URL asynchronously. If the element starts
 * streaming from the plain URL and we later swap in the blob, the element
 * reloads and playback restarts from zero. So the source may only upgrade from
 * the plain URL to the cached blob while playback has not started yet
 * (`allowUpgrade`); once the clip is actually playing the source is frozen.
 *
 * A change of `src` (a new video or audio clip) re-evaluates the choice.
 * `blobUrl` must be a live URL for the current `src` - useMediaCache
 * guarantees this by returning null both during the transition render and
 * after it has revoked a blob, so neither a stale blob from the previous clip
 * nor a revoked one from an earlier visit to this clip can be locked in.
 */
export const useLockedMediaSrc = (
  src: string | undefined,
  blobUrl: string | null,
  allowUpgrade: boolean,
): string | undefined => {
  const locked = useRef<{ key?: string; url?: string }>({});

  if (locked.current.key !== src) {
    locked.current = { key: src, url: blobUrl || src };
  } else if (allowUpgrade && blobUrl && locked.current.url !== blobUrl) {
    locked.current.url = blobUrl;
  }

  return locked.current.url;
};
